import { useState, useEffect } from 'react';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Clock } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useProfile } from '@/lib/providers';

interface ContentOption {
  id: string;
  title: string;
  image_url: string | null;
  durationMinutes: number;
  source: 'content' | 'exercise';
}

interface PlanTeamMeditationDialogProps {
  teamId: string;
  onCreated?: () => void;
}

const MEDITATION_TYPES = ['meditation', 'journey', 'minutjes'];

export function PlanTeamMeditationDialog({
  teamId,
  onCreated,
}: PlanTeamMeditationDialogProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [time, setTime] = useState('12:00');
  const [selectedItem, setSelectedItem] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { profile } = useProfile();
  const [meditations, setMeditations] = useState<ContentOption[]>([]);
  const [exercises, setExercises] = useState<ContentOption[]>([]);

  useEffect(() => {
    // Fetch meditations from content table
    supabase
      .from('content')
      .select('id, title, image_url, content_configs(id)')
      .eq('published', true)
      .in('content_type', MEDITATION_TYPES)
      .order('title')
      .then(({ data }) => {
        if (data) {
          const withAudio = data.filter((c: any) => c.content_configs?.length > 0);
          setMeditations(
            withAudio.map((c: any) => ({
              id: c.id,
              title: c.title,
              image_url: c.image_url,
              durationMinutes: 20,
              source: 'content' as const,
            })),
          );
        }
      });

    // Fetch exercises from exercises table
    supabase
      .from('exercises')
      .select('id, title, image_url, content_id')
      .not('content_id', 'is', null)
      .order('sort_order')
      .then(({ data }) => {
        if (data) {
          setExercises(
            data.map((e: any) => ({
              id: e.id,
              title: e.title,
              image_url: e.image_url,
              durationMinutes: 20,
              source: 'exercise' as const,
            })),
          );
        }
      });
  }, []);

  const allItems = [...meditations, ...exercises];

  const handleSubmit = async () => {
    if (!date || !selectedItem || !profile) return;

    const found = allItems.find((m) => m.id === selectedItem);
    const meditationTitle = found?.title ?? 'Activiteit';
    const durationMinutes = found?.durationMinutes ?? 20;
    const imageUrl = found?.image_url ?? null;
    const activityType = found?.source === 'exercise' ? 'exercise' : 'meditation';

    const [hours, minutes] = time.split(':').map(Number);
    const scheduledAt = new Date(date);
    scheduledAt.setHours(hours, minutes, 0, 0);

    setSubmitting(true);
    try {
      const { error } = await supabase.from('team_meditations').insert({
        team_id: teamId,
        created_by: profile.id,
        meditation_title: meditationTitle,
        scheduled_at: scheduledAt.toISOString(),
        duration_minutes: durationMinutes,
        image_url: imageUrl,
        activity_type: activityType,
      });

      if (error) throw error;

      setOpen(false);
      setDate(undefined);
      setTime('12:00');
      setSelectedItem('');
      onCreated?.();
    } catch (error) {
      console.error('Error planning meditation:', error);
      alert('Er ging iets mis bij het plannen van de activiteit.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
          Plan een Team Activiteit
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Plan een Team Activiteit</DialogTitle>
          <DialogDescription>
            Selecteer een meditatie of oefening, datum en tijd voor je team.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="meditation">Meditatie</Label>
            <Select
              value={meditations.some((m) => m.id === selectedItem) ? selectedItem : ''}
              onValueChange={setSelectedItem}
            >
              <SelectTrigger id="meditation">
                <SelectValue placeholder="Selecteer een meditatie uit de app" />
              </SelectTrigger>
              <SelectContent>
                {meditations.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exercise">Oefening</Label>
            <Select
              value={exercises.some((e) => e.id === selectedItem) ? selectedItem : ''}
              onValueChange={setSelectedItem}
            >
              <SelectTrigger id="exercise">
                <SelectValue placeholder="Selecteer een oefening uit de app" />
              </SelectTrigger>
              <SelectContent>
                {exercises.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label>Datum</Label>
            <div className="border rounded-md p-2 flex justify-center">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                initialFocus
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return date < today;
                }}
                className="scale-110 transform origin-center my-2"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="time">Tijd</Label>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <Input
                id="time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={submitting || !selectedItem || !date || !time}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {submitting ? 'Bezig...' : 'Stuur een uitnodiging naar je Team'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
